import { useScript } from '#imports'

import { noop } from '@backmarket/utils/function/noop'

import { SIGNIFYD_LIBRARY_URL } from '../config/signifyd/constants'
import {
  getOrSetSignifydSessionId,
  resetSignifydSessionId,
} from '../utils/signifydSessionId'

type UseSignifydOptions = {
  /**
   * When to trigger the script loading.
   * - `'onNuxtReady'`: Automatically load when Nuxt is ready (default)
   * - `'manual'`: Load manually by calling the `load()` function
   */
  trigger?: 'onNuxtReady' | 'manual'
}

/**
 * Returns a unique session ID for Signifyd. Loads the library if needed.
 *
 * @example
 * // Auto-load on Nuxt ready (default)
 * const { load, onLoaded } = useSignifyd()
 * onLoaded(() => {
 *   console.log('Signifyd loaded')
 * })
 *
 * @example
 * // Manual loading
 * const { load } = useSignifyd({ trigger: 'manual' })
 * await load()
 */
export const useSignifyd = (options: UseSignifydOptions = {}) => {
  if (import.meta.server) {
    // We need cookies to store the session id
    // to simplify we don't load the library on the server
    return { load: noop, onLoaded: noop, reset: noop }
  }

  const { trigger = 'onNuxtReady' } = options
  const sessionId = getOrSetSignifydSessionId()
  const { load, status, onLoaded } = useScript(
    {
      'data-order-session-id': sessionId,
      id: 'sig-api',
      src: SIGNIFYD_LIBRARY_URL,
    },
    {
      trigger,
    },
  )

  return {
    /** Load the signifyd library if needed and return the session id */
    load: async () => {
      if (status.value === 'loaded') {
        return sessionId
      }

      try {
        await load()

        return sessionId
      } catch {
        // This script can be blocked by extensions like Ghostery.
        // To avoid blocking the payment it will make a silent catch
        // Also, useScript should already have logged the error
      }

      return null
    },

    /** Register a callback to be called when the script is loaded */
    onLoaded,

    /** Reset the signifyd session id */
    reset: async () => {
      resetSignifydSessionId()
    },
  }
}
